import {
  Building2,
  type LucideIcon,
  ShieldCheck,
  Sparkles,
  Store,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { Role } from "@/types"

const ICONS: Record<Role, LucideIcon> = {
  admin: ShieldCheck,
  agency: Building2,
  brand: Store,
  creator: Sparkles,
}

interface RoleBadgeProps {
  role: Role
  className?: string
  /** Hide the textual label — keep only the icon. The aria-label still announces the role. */
  iconOnly?: boolean
}

/**
 * Muted role chip rendered in admin + brand/agency shell headers.
 * Uses `secondary` variant so it sits in OKS chrome — never brand-colored.
 *
 * Color is never the sole signal — every role gets a distinct icon + label.
 */
export function RoleBadge({
  role,
  className,
  iconOnly = false,
}: RoleBadgeProps) {
  const { t } = useTranslation()
  const label = t(`roles.${role}`)
  const Icon = ICONS[role]

  return (
    <Badge
      variant="secondary"
      role="status"
      aria-label={t("role_badge.aria_label", { role: label })}
      className={cn("gap-1.5", className)}
      data-testid={`role-badge-${role}`}
    >
      <Icon aria-hidden="true" />
      {!iconOnly && <span>{label}</span>}
    </Badge>
  )
}
